import { useCallback, useEffect, useRef, useState } from 'react'

interface UseAutoScrollbarOptions {
  /** Duration scrollbar stays visible after scrolling (ms) */
  scrollDuration?: number
  /** Duration scrollbar stays visible on flash trigger (ms) */
  flashDuration?: number
}

interface UseAutoScrollbarReturn {
  /** Ref to attach to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Whether the scrollbar should be visible */
  scrollbarVisible: boolean
  /** Whether the top fade gradient should be shown */
  showTopFade: boolean
  /** Whether the bottom fade gradient should be shown */
  showBottomFade: boolean
  /** onScroll handler to attach to the scrollable container */
  onScroll: () => void
  /** Trigger a brief scrollbar flash (e.g. on content change) */
  flash: () => void
}

/**
 * Hook for macOS-like auto-hiding scrollbar with fade indicators.
 *
 * Scrollbar is hidden by default and briefly appears on scroll or
 * when `flash()` is called. Fade gradients indicate overflow.
 *
 * @example
 * const { containerRef, scrollbarVisible, showTopFade, showBottomFade, onScroll } = useAutoScrollbar()
 *
 * <div ref={containerRef} className={`my-scroll-area${scrollbarVisible ? ' auto-scrollbar--visible' : ''}`} onScroll={onScroll}>
 *   {children}
 * </div>
 * {showTopFade && <div className="auto-scrollbar__fade auto-scrollbar__fade--top" />}
 * {showBottomFade && <div className="auto-scrollbar__fade auto-scrollbar__fade--bottom" />}
 */
export function useAutoScrollbar(options: UseAutoScrollbarOptions = {}): UseAutoScrollbarReturn {
  const { scrollDuration = 1000, flashDuration = 1500 } = options

  const containerRef = useRef<HTMLDivElement>(null)
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)
  const [scrollbarVisible, setScrollbarVisible] = useState(false)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef = useRef<number | null>(null)

  const updateFades = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setShowTopFade(el.scrollTop > 0)
    setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }, [])

  const showScrollbarBriefly = useCallback((duration: number) => {
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    setScrollbarVisible(true)
    scrollTimerRef.current = setTimeout(() => setScrollbarVisible(false), duration)
  }, [])

  // Throttle scroll handler to one update per frame
  const onScroll = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      updateFades()
      showScrollbarBriefly(scrollDuration)
    })
  }, [updateFades, showScrollbarBriefly, scrollDuration])

  const flash = useCallback(() => {
    showScrollbarBriefly(flashDuration)
    requestAnimationFrame(updateFades)
  }, [showScrollbarBriefly, flashDuration, updateFades])

  // Update fades on container resize AND when the scrollable content
  // changes size (e.g. a page switch that caps page height so the content
  // suddenly fits without overflow — the container itself doesn't resize in
  // that case, so we also observe its direct children).
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(updateFades)
    observer.observe(el)
    for (const child of Array.from(el.children)) {
      observer.observe(child)
    }
    // Watch for added/removed children so dynamically mounted pages are
    // observed too.
    const mutationObserver = new MutationObserver(() => {
      updateFades()
      observer.disconnect()
      observer.observe(el)
      for (const child of Array.from(el.children)) {
        observer.observe(child)
      }
    })
    mutationObserver.observe(el, { childList: true })
    return () => {
      observer.disconnect()
      mutationObserver.disconnect()
    }
  }, [updateFades])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return { containerRef, scrollbarVisible, showTopFade, showBottomFade, onScroll, flash }
}
